import { useAppDispatch, useAppSelector } from "../../../../../Redux/redux-store";
import { GetObject } from "../../../../../Redux/Reducers/redux-slice-asset";
import { GetPanelObject } from "../../../../../Redux/Reducers/redux-slice-drive";
import { ObjectType } from "../../../../../Core/Api/Types/object-type";
import { Box, BoxProps, Stack, Typography } from "@mui/material";
// import Grid from "@mui/material/Unstable_Grid2";
import { ObjectLayer } from "../../../../../Core/Api/Types/object-layer";
import { SetPlayerInput } from "../../../../../Redux/Reducers/redux-slice-react-player";
import { assetService, cacheService } from "../../../../../Core/Api/services";
import React, { useEffect, useMemo, useState } from "react";
import {
  IPreviewTabProps,
  PanelType,
  PreviewTabs,
} from "../../../../../Components/AssetPreview/PreviewTabs/preview-tabs";
import {
  AudioPlayer,
  FilePlayer,
  ImagePlayer,
  SubtitlePlayer,
  TarPlayer,
  VideoAndTarPlayer,
  VideoPlayer,
} from "../../../../../Components/AssetPreview/Players/asset-single-players";
import { DbAssetObjectInfos } from "../../../../../Core/Api/Types/db-asset-object-infos";
import Panel from "../../../../../Components/Panels/Panel/panel";
import { PlatformMode } from "../../../../../Core/Api/Types/platform-mode";
import { FrameToTimecode } from "../../../../../Utils/Timecode/ReactPlayer";
import {
  GetCreatingOrEditingTimelineEvent,
  GetTimelineClips,
  GetTimelineMarkers,
  SetActiveTimeline,
  SetCreatingOrEditingTimelineEvent,
  SetTimelineClips,
  SetTimelineLoading,
  SetTimelineMarkers,
  SetTimelineStoryboard,
} from "../../../../../Redux/Reducers/redux-slice-timeline";
import { MapQCMarkerData } from "../../../../../Components/Cards/QCMarkerCard/qc-marker-card";
import { DbMetadataFieldInfos } from "../../../../../Core/Api/Types/db-metadata-field-infos";
import { GetMetadataByTagName, IsDriveObject } from "../../../../../Core/Utils/asset-object-utils";
import { ClipCardProps, ConvertAssetClipToClipCard } from "../../../../../Components/Cards/ClipCard/clip-card";
import { DbMarkerCategory } from "../../../../../Core/Api/Types/db-marker-category";
import { PanelTimeline } from "../../../../../Components/Panels/TimelinePanel/panel-timeline";
import { useTranslation } from "react-i18next";
import { t } from "i18next";
import { Preview } from "@mui/icons-material";

type HeaderActions = {
  play: boolean;
  share: boolean;
  order: boolean;
};

export enum PreviewContext {
  DriveFolderPreview = "driveFolderPreview",
  DriveCollectionPreview = "driveCollectionPreview",
  DriveEnlarged = "driveEnlarged",
  DistribInventoryPreview = "distribInventoryPreview",
  DistribCollectionPreview = "distribCollectionPreview",
  DistribEnlarged = "distribEnlarged",
  DistribEnlargedTabs = "distribEnlargedTabs",
  Title = "title",
  Share = "share",
  Modal = "modal",
}

export interface AssetSinglePreviewProps {
  context?: PreviewContext;
  url?: string;
}

export interface AssetSinglePreviewHeaderProps {
  asset: DbAssetObjectInfos;
  actions: HeaderActions;
}

export interface AssetSinglePreviewContainerProps extends BoxProps {
  context?: PreviewContext;
  player: React.ReactNode;
  tabs: React.ReactNode;
  timeline?: React.ReactNode;
  assetId: number;
}

function AssetSinglePreview(props: AssetSinglePreviewProps) {
  const { t } = useTranslation();
  const { context = PreviewContext.DistribEnlarged } = props;
  const platformMode = cacheService.GetPlatformMode();
  const asset = useAppSelector(
    context === PreviewContext.DriveEnlarged ||
      context === PreviewContext.DriveFolderPreview ||
      context === PreviewContext.DriveCollectionPreview ||
      context === PreviewContext.DistribInventoryPreview ||
      context === PreviewContext.DistribCollectionPreview
      ? GetPanelObject
      : GetObject
  );

  const dispatch = useAppDispatch();

  const markers = useAppSelector(GetTimelineMarkers);
  const clips = useAppSelector(GetTimelineClips);

  const [clipsCount, setClipsCount] = useState(0);
  const [markersCount, setMarkersCount] = useState(0);

  // Load Markers and clips
  useEffect(() => {
    // Reset timeline
    dispatch(SetCreatingOrEditingTimelineEvent({ creatingOrEditing: false }));
    dispatch(SetActiveTimeline(null));

    if (!asset || ![ObjectType.Video, ObjectType.Audio, ObjectType.ImageSequence].includes(asset.type)) return;
    dispatch(SetTimelineLoading(true));

    assetService.GetMarkersByObjectId(asset.id).then((data) => {
      if (data.markers) {
        let frameDuration = 0;
        if (data.timecodeOutFileFrames !== null && data.timecodeInFileFrames !== null)
          frameDuration = data.timecodeOutFileFrames - data.timecodeInFileFrames;
        data.markers
          ?.sort((a, b) => {
            return a.id - b.id;
          })
          .forEach((x) => {
            x.timecodeIn = x?.timecodeIn || FrameToTimecode(x.frameIn === null ? 0 : x.frameIn, x!.frameRate!);
            x.timecodeOut =
              x?.timecodeOut || FrameToTimecode(x.frameOut === null ? frameDuration : x.frameOut, x!.frameRate!);
          });

        const markers = MapQCMarkerData(
          data.markers.filter(
            (x) =>
              x.category !== DbMarkerCategory.SceneChange &&
              x.category !== DbMarkerCategory.ThumbPgm &&
              x.category !== DbMarkerCategory.ThumbRef
          ),
          frameDuration
        );

        dispatch(SetTimelineMarkers(markers));

        dispatch(
          SetTimelineStoryboard(
            MapQCMarkerData(
              data.markers.filter((x) => x.category === DbMarkerCategory.SceneChange),
              frameDuration
            )
          )
        );
        dispatch(SetTimelineLoading(false));
      }
    });

    // Cache
    const metadata: DbMetadataFieldInfos[] = cacheService.GetMetadata();
    const frameRateAsset: number =
      asset && metadata.length > 0 ? GetMetadataByTagName(asset!, "techFrameRate", metadata)?.value : 25;

    assetService.GetClipsByObjectId(asset.id).then((data) => {
      if (data.results) {
        const clips = data.results
          .filter((y) => y.type === ObjectType.VideoClip || y.type === ObjectType.AudioClip)
          .map<ClipCardProps>((y) => ConvertAssetClipToClipCard(y, frameRateAsset));
        dispatch(SetTimelineClips(clips));
      }
    });
  }, [asset?.id]);

  // Update Markers & Clips count on change
  useEffect(() => {
    setClipsCount(clips.length);
    setMarkersCount(markers.length);
  }, [markers, clips]);

  useEffect(() => {
    if (!asset) return;

    if (
      [ObjectType.Video, ObjectType.VideoClip, ObjectType.Audio, ObjectType.ImageSequence].includes(asset.type) ||
      asset.layer === ObjectLayer.Content
    ) {
      if (asset.id) {
        dispatch(SetPlayerInput({ objectId: asset.id, episode: null, shareMediaKey: null }));
      }
    }
  }, [asset]);

  const preview = useMemo(() => {
    if (asset === undefined)
      return (
        <Stack sx={{ py: 2 }}>
          <Typography variant="subtitle2" color="text.tier">
            {t("page:body.noPreviewAvailable")}
          </Typography>
        </Stack>
      );

    const tabs = getTabs(context, asset, { clips: clipsCount, markers: markersCount });

    if (asset.layer === ObjectLayer.Content) {
      return (
        <AssetSinglePreviewContainer
          assetId={asset.id}
          player={<VideoPlayer context={context} asset={asset} />}
          tabs={<PreviewTabs context={context} asset={asset} tabs={tabs} />}
          context={context}
        />
      );
    }

    switch (asset.type) {
      case ObjectType.Video:
      case ObjectType.VideoClip:
      case ObjectType.DcpPackage:
      case ObjectType.DolbyAtmosPackage:
        return (
          <AssetSinglePreviewContainer
            assetId={asset.id}
            player={<VideoPlayer context={context} asset={asset} />}
            tabs={<PreviewTabs context={context} asset={asset} tabs={tabs} />}
            context={context}
          />
        );
      case ObjectType.Audio:
        return (
          <AssetSinglePreviewContainer
            assetId={asset.id}
            player={<AudioPlayer context={context} asset={asset} />}
            tabs={<PreviewTabs context={context} asset={asset} tabs={tabs} />}
            context={context}
          />
        );
      case ObjectType.Image:
        return (
          <AssetSinglePreviewContainer
            assetId={asset.id}
            player={<ImagePlayer asset={asset} />}
            tabs={<PreviewTabs context={context} asset={asset} tabs={tabs} />}
            context={context}
          />
        );
      case ObjectType.Subtitle:
        return (
          <AssetSinglePreviewContainer
            assetId={asset.id}
            player={<SubtitlePlayer asset={asset} context={context} />}
            tabs={<PreviewTabs context={context} asset={asset} tabs={tabs} />}
            context={context}
            overflow={"auto"}
            sx={{ scrollbarWidth: "thin" }}
          />
        );
      case ObjectType.Document:
        return (
          <AssetSinglePreviewContainer
            assetId={asset.id}
            player={<FilePlayer asset={asset} />}
            tabs={<PreviewTabs context={context} asset={asset} tabs={tabs} />}
            context={context}
            sx={{ overflow: "hidden", height: "calc(100% - 32px)" }}
          />
        );
      case ObjectType.Generic:
        return (
          <AssetSinglePreviewContainer
            assetId={asset.id}
            player={<TarPlayer asset={asset} />}
            tabs={<PreviewTabs context={context} asset={asset} tabs={tabs} />}
            context={context}
            sx={{ overflow: "hidden" }}
          />
        );
      case ObjectType.ImageSequence:
        return (
          <AssetSinglePreviewContainer
            assetId={asset.id}
            player={<VideoAndTarPlayer asset={asset} />}
            tabs={<PreviewTabs context={context} asset={asset} tabs={tabs} />}
            context={context}
          />
        );
      case ObjectType.Folder:
        return (
          <AssetSinglePreviewContainer
            assetId={asset.id}
            player={null}
            tabs={<PreviewTabs context={context} asset={asset} tabs={tabs} />}
            context={context}
          />
        );
      default:
        return (
          <Stack sx={{ py: 2 }}>
            <Typography variant="subtitle2" color="text.tier">
              {t("page:body.noPreviewAvailable")}
            </Typography>
          </Stack>
        );
    }
  }, [asset, clipsCount, markersCount]);

  return <>{preview}</>;
}

export default AssetSinglePreview;

const AssetSinglePreviewContainer: React.FC<AssetSinglePreviewContainerProps> = (props) => {
  const { player, tabs, context = PreviewContext.DistribEnlarged, assetId, ...otherProps } = props;
  const isCreatingTimelineEvent = useAppSelector(GetCreatingOrEditingTimelineEvent);

  return (
    <Box width={"100%"} display={"flex"} flexWrap={"nowrap"} {...otherProps}>
      <Box width={"100%"} borderRadius={"6px"}>
        {player}
      </Box>
      {isCreatingTimelineEvent ? (
        <PanelTimeline parentId={assetId} context={context} />
      ) : (
        <Panel noWrapper open sx={{ height: "100%" }}>
          <Box sx={{ display: "flex", borderRadius: "6px", height: "100%" }}>{tabs}</Box>
        </Panel>
      )}
    </Box>
  );
};

function getTabs(
  context: AssetSinglePreviewProps["context"],
  asset: DbAssetObjectInfos,
  timelineItems?: { clips: number; markers: number }
): IPreviewTabProps[] {
  const tabs: IPreviewTabProps[] = [];

  tabs.push({ label: t("common:metadata"), value: PanelType.METADATA });

  switch (asset.type) {
    case ObjectType.Video:
    case ObjectType.DcpPackage:
    case ObjectType.DolbyAtmosPackage:
      if (context === PreviewContext.DriveFolderPreview || context === PreviewContext.DriveEnlarged) {
        tabs.push({
          label: t("common:access"),
          value: PanelType.ACCESS,
        });
      }
      if (context === PreviewContext.DistribEnlarged) {
        let children: IPreviewTabProps[] = [
          { label: t("common:clips"), count: timelineItems?.clips, value: PanelType.TIMELINE_CLIPS },
          { label: t("page:tab.label.transcript"), value: PanelType.TIMELINE_TRANSCRIPT },
          { label: t("page:tab.label.Markers"), count: timelineItems?.markers, value: PanelType.TIMELINE_MARKERS },
        ];

        if (IsDriveObject(asset))
          children.push({ label: t("page:tab.label.storyboard"), value: PanelType.TIMELINE_STORYBOARD });

        tabs.push({
          label: t("button:timeline"),
          value: PanelType.TIMELINE,
          children: children,
        });
      }
      if (context === PreviewContext.DriveEnlarged) {
        tabs.push({
          label: t("button:timeline"),
          value: PanelType.TIMELINE,
          children: [
            { label: t("common:clips"), count: timelineItems?.clips, value: PanelType.TIMELINE_CLIPS },
            { label: t("page:tab.label.transcript"), value: PanelType.TIMELINE_TRANSCRIPT },
            { label: t("page:tab.label.Markers"), count: timelineItems?.markers, value: PanelType.TIMELINE_MARKERS },
            { label: t("page:tab.label.storyboard"), value: PanelType.TIMELINE_STORYBOARD },
          ],
        });
      }
      break;
    case ObjectType.Audio:
      if (context === PreviewContext.DriveFolderPreview || context === PreviewContext.DriveEnlarged) {
        tabs.push({
          label: t("common:access"),
          value: PanelType.ACCESS,
        });
      }
      if (context === PreviewContext.DriveEnlarged || context === PreviewContext.DistribEnlarged) {
        tabs.push({
          label: t("button:timeline"),
          value: PanelType.TIMELINE,
          children: [
            { label: t("common:clips"), count: timelineItems?.clips, value: PanelType.TIMELINE_CLIPS },
            { label: t("page:tab.label.transcript"), value: PanelType.TIMELINE_TRANSCRIPT },
            { label: t("page:tab.label.Markers"), count: timelineItems?.markers, value: PanelType.TIMELINE_MARKERS },
          ],
        });
      }
      break;
    case ObjectType.Subtitle:
    case ObjectType.Document:
    case ObjectType.Generic:
    case ObjectType.ImageSequence:
    case ObjectType.Image:
      if (PreviewContext.DriveFolderPreview || context === PreviewContext.DriveEnlarged) {
        tabs.push({
          label: t("common:access"),
          value: PanelType.ACCESS,
        });
      }
      break;
    case ObjectType.Folder:
      if (context === PreviewContext.DriveFolderPreview || context === PreviewContext.DriveEnlarged) {
        tabs.push(
          {
            label: t("common:access"),
            value: PanelType.ACCESS,
          },
          {
            label: t("route:label.quotas"),
            value: PanelType.QUOTAS,
          }
        );
      }
  }

  return tabs;
}
